import { computed, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import { B2BOffer, DataContainer, Offer } from '../offer/OfferInterfaces';
import { CommonAPI } from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import LanguageStore from './LanguageStore';
import ProposalStore from './ProposalStore';
import RootStore from './RootStore';
import SsoStore from './SsoStore';

class PersonalOffersStore {
  public customerIdReaction: IReactionDisposer = null;
  @observable accessor offers: DataContainer<Offer[]> = {
    status: FetchStatus.UNINITIALIZED,
    data: []
  };

  @observable accessor b2bPersonalOffer: DataContainer<B2BOffer> = {
    status: FetchStatus.UNINITIALIZED,
    data: null
  };

  @observable accessor b2bOfferGravityModalOpen: boolean = false;

  private ssoStore: SsoStore;
  private languageStore: LanguageStore;
  private proposalStore: ProposalStore;
  private subscribers: number = 0;

  constructor(rootStore: RootStore) {
    this.ssoStore = rootStore.ssoStore;
    this.languageStore = rootStore.languageStore;
    this.proposalStore = rootStore.proposalStore;
  }

  public subscribe() {
    this.subscribers = this.subscribers + 1;
    if (!this.customerIdReaction) {
      this.customerIdReaction = reaction(
        () => this.ssoStore.customerId,
        () => {
          this.ssoStore.fetchIsEmployee();
          this.fetchOffers();
        },
        { fireImmediately: true }
      );
    }
  }

  public unsubscribe() {
    this.subscribers = this.subscribers - 1;
    if (!this.subscribers && this.customerIdReaction) {
      this.customerIdReaction();
      this.customerIdReaction = null;
    }
  }

  @computed
  get offersFetchFailed(): boolean {
    return this.offers.status === FetchStatus.ERROR;
  }

  @computed
  get offersFetching(): boolean {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.offers.status);
  }

  @computed
  get hasOffers(): boolean {
    return this.offers.status === FetchStatus.FETCHED && this.offers.data?.length > 0;
  }

  @computed
  get hasB2BOffer(): boolean {
    return !!this.b2bPersonalOffer.data?.offerIds?.length;
  }

  public async fetchOffers() {
    this.reset();
    runInAction(() => {
      this.proposalStore.reset();
    });
    if (this.ssoStore.customerId) {
      return runInAction(() => {
        if (this.ssoStore.hasLegalOrServiceTeliaMandate) {
          return Promise.all([this.fetchPersonalOffers(), this.proposalStore.fetchProposals()]);
        } else {
          this.offers.status = FetchStatus.ERROR;
        }
      });
    }
  }

  public updatePersonalOfferStatus = async (offerIds: number[], status: string) => {
    if (!offerIds?.length) {
      return Promise.resolve();
    }
    return CommonAPI.put(`/offers/sf/post-offer-status/${status}`, null, { params: { offerIds } });
  };

  public presentSfOffers = async (recommendationItemIds: number[]) => {
    return this.updatePersonalOfferStatus(recommendationItemIds, 'PRESENTED');
  };

  public rejectOffer(offer: Offer) {
    return this.updatePersonalOfferStatus([offer.recommendationItemId], 'REJECTED');
  }

  public acceptOffer(recommendationItemId: number) {
    return this.updatePersonalOfferStatus([recommendationItemId], 'ACCEPTED');
  }

  public presentOffer(offer: Offer) {
    return this.updatePersonalOfferStatus([offer.recommendationItemId], 'PRESENTED');
  }

  public rejectB2BOffers() {
    this.updatePersonalOfferStatus(this.b2bPersonalOffer.data.offerIds, 'REJECTED');
    this.b2bPersonalOffer.data.offerIds = [];
  }

  public sortPersonalOffersByPriority(offers: Offer[]): Offer[] {
    const offersWithNoPriority: Offer[] = offers.filter((offer) => offer.priority === null);
    const prioritizedOffers = offers.filter((offer) => offer.priority || offer.priority === 0).sort((offerA, offerB) => offerA.priority - offerB.priority);
    return prioritizedOffers.concat(offersWithNoPriority);
  }

  private reset() {
    runInAction(() => {
      this.offers = { status: FetchStatus.UNINITIALIZED, data: [] };
    });
  }

  private fetchPersonalOffers = async () => {
    runInAction(() => {
      this.offers.status = FetchStatus.FETCHING;
    });
    const lang = this.languageStore.currentLanguage;
    try {
      const response = await CommonAPI.get(`/offers/${lang}`);
      runInAction(() => {
        this.offers.data = response.data.offers;
        this.b2bPersonalOffer.data = response.data.b2bOffer;
        this.offers.status = FetchStatus.FETCHED;
        this.b2bPersonalOffer.status = FetchStatus.FETCHED;
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.offers.status = FetchStatus.ERROR;
      });
    }
  };
}

export default PersonalOffersStore;
